import { BaseEntity } from './base.entity';

export type ChangeProposalStatus = 'pending' | 'approved' | 'rejected';

export class ChangeProposal extends BaseEntity {
  id: string;
  project_id: string;
  chat_id?: string;
  title: string;
  description?: string;
  status: ChangeProposalStatus;
  created_by: string;
  reviewed_by?: string;
  created_at: Date;
  reviewed_at?: Date;

  constructor(data: {
    id: string;
    project_id: string;
    chat_id?: string;
    title: string;
    description?: string;
    status: ChangeProposalStatus;
    created_by: string;
    reviewed_by?: string;
    created_at: Date;
    reviewed_at?: Date;
  }) {
    super();
    this.id = data.id;
    this.project_id = data.project_id;
    this.chat_id = data.chat_id;
    this.title = data.title;
    this.description = data.description;
    this.status = data.status;
    this.created_by = data.created_by;
    this.reviewed_by = data.reviewed_by;
    this.created_at = data.created_at;
    this.reviewed_at = data.reviewed_at;
    this.setOriginalData(this.toData());
  }

  /**
   * Set title
   */
  setTitle(title: string): void {
    this.title = title;
    this.markAsDirty();
  }

  /**
   * Set description
   */
  setDescription(description: string): void {
    this.description = description;
    this.markAsDirty();
  }

  /**
   * Approve the proposal
   */
  approve(reviewedBy: string): void {
    this.status = 'approved';
    this.reviewed_by = reviewedBy;
    this.reviewed_at = new Date();
    this.markAsDirty();
  }

  /**
   * Reject the proposal
   */
  reject(reviewedBy: string): void {
    this.status = 'rejected';
    this.reviewed_by = reviewedBy;
    this.reviewed_at = new Date();
    this.markAsDirty();
  }

  /**
   * Check if proposal is pending
   */
  isPending(): boolean {
    return this.status === 'pending';
  }

  /**
   * Check if proposal is approved
   */
  isApproved(): boolean {
    return this.status === 'approved';
  }

  /**
   * Check if proposal is rejected
   */
  isRejected(): boolean {
    return this.status === 'rejected';
  }

  /**
   * Convert to plain object for database operations
   */
  toData(): any {
    return {
      id: this.id,
      project_id: this.project_id,
      chat_id: this.chat_id,
      title: this.title,
      description: this.description,
      status: this.status,
      created_by: this.created_by,
      reviewed_by: this.reviewed_by,
      created_at: this.created_at,
      reviewed_at: this.reviewed_at,
    };
  }

  /**
   * Create instance from database data
   */
  static fromData(data: any): ChangeProposal {
    const proposal = new ChangeProposal({
      id: data.id,
      project_id: data.project_id,
      chat_id: data.chat_id,
      title: data.title,
      description: data.description,
      status: data.status,
      created_by: data.created_by,
      reviewed_by: data.reviewed_by,
      created_at: new Date(data.created_at),
      reviewed_at: data.reviewed_at ? new Date(data.reviewed_at) : undefined,
    });
    proposal.markAsClean();
    return proposal;
  }
}

export interface ChangeProposalInsert {
  id?: string;
  project_id: string;
  chat_id?: string;
  title: string;
  description?: string;
  status?: ChangeProposalStatus;
  created_by: string;
  reviewed_by?: string;
  created_at?: Date;
  reviewed_at?: Date;
}

export interface ChangeProposalUpdate {
  title?: string;
  description?: string;
  status?: ChangeProposalStatus;
  reviewed_by?: string;
  reviewed_at?: Date;
}

export class ChangeProposalDocument extends BaseEntity {
  id: string;
  change_proposal_id: string;
  document_id: string;
  original_content: string;
  proposed_content: string;
  created_at: Date;
  processing_status?: 'success' | 'too_large';
  processing_notes?: string | null;

  constructor(data: {
    id: string;
    change_proposal_id: string;
    document_id: string;
    original_content: string;
    proposed_content: string;
    created_at: Date;
    processing_status?: 'success' | 'too_large';
    processing_notes?: string | null;
  }) {
    super();
    this.id = data.id;
    this.change_proposal_id = data.change_proposal_id;
    this.document_id = data.document_id;
    this.original_content = data.original_content;
    this.proposed_content = data.proposed_content;
    this.created_at = data.created_at;
    this.processing_status = data.processing_status;
    this.processing_notes = data.processing_notes;
    this.setOriginalData(this.toData());
  }

  /**
   * Convert to plain object for database operations
   */
  toData(): any {
    return {
      id: this.id,
      change_proposal_id: this.change_proposal_id,
      document_id: this.document_id,
      original_content: this.original_content,
      proposed_content: this.proposed_content,
      created_at: this.created_at,
      processing_status: this.processing_status,
      processing_notes: this.processing_notes,
    };
  }

  /**
   * Create instance from database data
   */
  static fromData(data: any): ChangeProposalDocument {
    const document = new ChangeProposalDocument({
      id: data.id,
      change_proposal_id: data.change_proposal_id,
      document_id: data.document_id,
      original_content: data.original_content,
      proposed_content: data.proposed_content,
      created_at: new Date(data.created_at),
      processing_status: data.processing_status,
      processing_notes: data.processing_notes,
    });
    document.markAsClean();
    return document;
  }
}

export interface ChangeProposalDocumentInsert {
  id?: string;
  change_proposal_id: string;
  document_id: string;
  original_content: string;
  proposed_content: string;
  created_at?: Date;
  processing_status?: 'success' | 'too_large';
  processing_notes?: string | null;
}